import { useEffect, useState } from 'react';

import {
	CONNECTOR_AUTH_RATE_TRAIT,
	fetchSiteTraits,
	normalizeTapKeys,
	parseProductValueTrait,
	type Trait,
} from '@atlassian/conversation-assistant-service/services/tap-service';

import { SMARTLINK_APP_EXPOSURE_RATE_TRAIT } from '../../tap-traits-client';

type RovoSettingsTapTraits = {
	loaded: boolean;
	smartlinkExposureRate: Record<string, number> | undefined;
	connectorAuthStats: Record<string, number> | undefined;
};

/**
 * Upper bound on how long we wait for TAP traits before rendering apps unranked.
 *
 * Consumers (e.g. the discovery modal) gate their UI on `loaded === true` to avoid showing apps
 * in a default order and then visibly re-ordering when stats arrive. If TAP is slow or down we
 * still need to surface the apps within a reasonable time. 3s is comfortably above typical TAP
 * latency while keeping the worst-case spinner short enough to not feel broken.
 */
const TAP_TRAITS_FETCH_TIMEOUT_MS = 3000;

const extractTrait = (traits: Trait[], traitName: string): Record<string, number> | undefined => {
	const normalized = normalizeTapKeys(parseProductValueTrait(traits, traitName));
	return Object.keys(normalized).length > 0 ? normalized : undefined;
};

export const useRovoSettingsTapTraits = (cloudId?: string): RovoSettingsTapTraits => {
	const [result, setResult] = useState<RovoSettingsTapTraits>({
		loaded: false,
		smartlinkExposureRate: undefined,
		connectorAuthStats: undefined,
	});

	useEffect(() => {
		if (!cloudId) {
			return;
		}
		let cancelled = false;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), TAP_TRAITS_FETCH_TIMEOUT_MS);

		fetchSiteTraits(cloudId, controller.signal)
			.then((traits) => {
				if (cancelled) {
					return;
				}
				setResult({
					loaded: true,
					smartlinkExposureRate: extractTrait(traits, SMARTLINK_APP_EXPOSURE_RATE_TRAIT),
					connectorAuthStats: extractTrait(traits, CONNECTOR_AUTH_RATE_TRAIT),
				});
			})
			.catch(() => {
				// Real fetch errors and the timeout abort both land here. Mark as loaded with no
				// stats so the UI falls back to an unranked render.
				if (cancelled) {
					return;
				}
				setResult({
					loaded: true,
					smartlinkExposureRate: undefined,
					connectorAuthStats: undefined,
				});
			})
			.finally(() => {
				clearTimeout(timeoutId);
			});

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
			controller.abort();
		};
	}, [cloudId]);

	return result;
};
